<template>
    <div class="guardian-selection-modal-wrapper">
        <Modal :visible="visible" @close="closeModal">
            <div class="modal-header-section">
                <h2 class="modal-header-title">AI Assistants</h2>
            </div>

            <div class="modal-body-section">
                <p class="text-muted">
                    Select an assistant for your topic of choice.
                </p>
                <div class="guardians-grid">
                    <div
                        v-for="guardian in filteredGuardians"
                        :key="guardian.id"
                        class="guardian-card"
                        :class="{
                            selected: selectedGuardian?.id === guardian.id,
                        }"
                        @click="selectGuardian(guardian)"
                    >
                        <DotLottieVue
                            v-if="isLottieAnimation(guardian.image)"
                            :data="guardian.image"
                            class="guardian-image"
                            loop
                            autoplay
                        />
                        <img
                            v-else-if="guardian.image"
                            :src="getImageSrc(guardian.image)"
                            :alt="guardian.name"
                            class="guardian-image"
                        />
                        <div v-else class="guardian-placeholder">
                            <i class="fi fi-robot"></i>
                        </div>

                        <div class="guardian-label">
                            {{ guardian.label }}
                        </div>
                        <p class="guardian-name text-muted">
                            {{ guardian.name }}
                        </p>
                    </div>
                </div>
            </div>
        </Modal>
    </div>
</template>

<script>
import Modal from '@/components/Modal.vue';
import { DotLottieVue } from '@lottiefiles/dotlottie-vue';
import { useGuardianStore } from '@/stores/guardian';
import { GuardianFilterMixin } from '@/mixins/GuardianFilterMixin.js';
import { GUARDIANS } from '@/config/guardians.js';

export default {
    name: 'GuardianSelectionModal',
    components: {
        Modal,
        DotLottieVue,
    },
    mixins: [GuardianFilterMixin],
    props: {
        visible: {
            type: Boolean,
            required: true,
        },
    },
    emits: ['close'],
    data() {
        return {
            guardianStore: null,
            selectedGuardian: null,
            guardians: GUARDIANS,
        };
    },
    async mounted() {
        this.guardianStore = useGuardianStore();
        await this.fetchAvailableAgents();
    },
    methods: {
        isLottieAnimation(image) {
            return (
                image &&
                typeof image === 'object' &&
                image.constructor === Object &&
                !image.default
            );
        },
        getImageSrc(image) {
            if (typeof image === 'string') {
                return image;
            }
            if (image && typeof image === 'object') {
                return image.default || image.src || '';
            }
            return '';
        },
        selectGuardian(guardian) {
            if (!guardian) return;
            this.selectedGuardian = guardian;
            this.guardianStore?.openNew(guardian);
            this.closeModal();
        },
        closeModal() {
            this.$emit('close');
        },
    },
};
</script>

<style>
/* Override modal width - unscoped to ensure it applies */
.guardian-selection-modal-wrapper .custom-modal {
    width: 560px !important;
    max-width: 95% !important;
    background-color: #eff6f2;
}
@media (min-width: 768px) {
    .guardian-selection-modal-wrapper .custom-modal {
        margin-left: -44px;
        border-bottom-right-radius: 0;
        border-top-right-radius: 0;
    }
}
</style>

<style scoped>
.modal-header-section {
    background-color: var(--neutral-500);
    padding: var(--space-5);
    margin: calc(-1 * var(--space-10)) calc(-1 * 3rem) var(--space-5);
    border-radius: var(--radius-default) var(--radius-default) 0 0;
}

.modal-header-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--black);
    margin: 0 0 var(--space-2) 0;
}

.modal-header-subtitle {
    font-size: 0.9375rem;
    color: var(--neutral-800);
    margin: 0;
}

.modal-body-section {
    padding: 0;
}

.guardians-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: flex-start;
}

.guardian-card {
    display: flex;
    width: 145px;
    height: 163px;
    padding: 8px;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    border-radius: 8px;
    background: var(--neutral-400);
    cursor: pointer;
    transition: all 0.2s ease;
    box-sizing: border-box;
}

.guardian-card:hover {
    border-radius: var(--radius-default, 8px);
    border: 2px solid var(--neutral-600, #e6e6e6);
    background: var(--neutral-400, #fafafa);
    box-shadow: 0 0 8px 0 var(--primary-200, #98b7a8);
}

.guardian-image {
    width: 80px;
    height: 80px;
    object-fit: contain;
    flex-shrink: 0;
    background-color: transparent;
    border-radius: var(--radius-default);
}

.guardian-placeholder {
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    color: var(--neutral-400);
    background-color: var(--white);
    border-radius: var(--radius-default);
}

.guardian-label {
    color: var(--black, #2d2d2d);
    font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    text-align: center;
}

.guardian-name {
    font-size: 12px;
    color: var(--neutral-600);
    text-align: center;
}

@media (max-width: 767.98px) {
    .modal-header-section {
        margin: calc(-1 * var(--space-4)) calc(-1 * 1rem) var(--space-4);
        padding: var(--space-4);
    }

    .guardians-grid {
        gap: var(--space-3);
    }

    .guardian-card {
        width: 120px;
        height: 140px;
    }

    .guardian-image {
        width: 60px;
        height: 60px;
    }

    .guardian-placeholder {
        width: 60px;
        height: 60px;
        font-size: 1.5rem;
    }
}
</style>
